import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

export function resolveFeeParentKeys(rows) {
  const decodeProperty = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };
  const propertiesByRow = new Map(rows.map((row) => {
    try {
      const parsed = JSON.parse(row.dataset.cartProperties || '{}') || {};
      const properties = Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed;
      return [row, Object.fromEntries(Object.entries(properties).map(([key, value]) =>
        [decodeProperty(key), decodeProperty(value)]
      ))];
    }
    catch { return [row, {}]; }
  }));
  for (const row of rows) {
    const properties = propertiesByRow.get(row);
    if (!properties._fee_parent_variant_id || !properties._fee_parent_properties) continue;
    try {
      const parentProperties = JSON.parse(properties._fee_parent_properties);
      const parentEntries = Object.entries(parentProperties).filter(([, value]) => value !== '' && value != null);
      const parent = rows.find((candidate) => {
        if (candidate === row || candidate.dataset.cartProductId !== String(properties._fee_parent_product_id) ||
            candidate.dataset.cartVariantId !== String(properties._fee_parent_variant_id)) return false;
        const candidateProperties = propertiesByRow.get(candidate);
        const candidateEntries = Object.entries(candidateProperties).filter(([, value]) => value !== '' && value != null);
        return candidateEntries.length === parentEntries.length &&
          parentEntries.every(([key, value]) => candidateProperties[key] === value);
      });
      if (parent) row.dataset.feeParentKey = parent.dataset.key;
    } catch {}
  }
  return rows;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @returns {HTMLTableRowElement[]} */
  #getCartItemRows() {
    return resolveFeeParentKeys(Array.from(this.querySelectorAll('tr[data-key]')).filter(
      (row) => row instanceof HTMLTableRowElement
    ));
  }

  /** @param {number} line */
  #getCartItemRow(line) {
    return this.#getCartItemRows().find((row) => row.dataset.cartLine === line.toString());
  }

  /** @param {number} line */
  #getQuantitySelector(line) {
    return this.querySelector(`input[data-cart-line="${line}"]`)?.closest('cart-quantity-selector-component');
  }

  #groupLinkedFeeRows() {
    const rows = this.#getCartItemRows();
    const rowsByKey = new Map(rows.map((row) => [row.dataset.key, row]));
    const feeRowsByParentKey = new Map();
    const feeRowsByParentGroup = new Map();

    for (const row of rows) {
      const parentKey = row.dataset.feeParentKey;
      const parentGroup = row.dataset.feeParentGroup;
      const targetMap = parentKey ? feeRowsByParentKey : parentGroup ? feeRowsByParentGroup : null;
      const targetKey = parentKey || parentGroup;
      if (!targetMap || !targetKey) continue;
      targetMap.set(targetKey, [...(targetMap.get(targetKey) || []), row]);
    }

    const insertRowsAfter = (anchor, linkedRows) => {
      if (!anchor?.parentNode || linkedRows.length === 0) return;
      const fragment = document.createDocumentFragment();
      linkedRows.forEach((row) => fragment.append(row));
      anchor.parentNode.insertBefore(fragment, anchor.nextSibling);
    };

    feeRowsByParentKey.forEach((linkedRows, parentKey) => {
      insertRowsAfter(rowsByKey.get(parentKey), linkedRows);
    });

    feeRowsByParentGroup.forEach((linkedRows, parentGroup) => {
      const parentRows = rows.filter(
        (row) => row.dataset.bulkOrderGroup === parentGroup && !row.dataset.feeParentKey && !row.dataset.feeParentGroup
      );
      insertRowsAfter(parentRows.at(-1), linkedRows);
    });
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#groupLinkedFeeRows();
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(async ({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#groupLinkedFeeRows();
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#groupLinkedFeeRows();
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    this.#groupLinkedFeeRows();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#groupLinkedFeeRows();
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Check minimum quantity requirement
    const quantitySelector = this.#getQuantitySelector(line);
    if (quantitySelector) {
      const minimumQuantity = parseInt(quantitySelector.dataset.minimumQuantity || '0');
      if (minimumQuantity > 0 && quantity < minimumQuantity) {
        // Revert to minimum quantity
        const quantityInput = quantitySelector.querySelector('input');
        if (quantityInput) {
          quantityInput.value = minimumQuantity.toString();
          quantitySelector.updateButtonStates?.();
        }

        // Show error message
        this.#handleCartMinimumQuantityError(line, minimumQuantity);
        return;
      }
    }

    const lineItemRow = this.#getCartItemRow(line);

    this.updateQuantity({
      line,
      lineKey: lineItemRow?.dataset.key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const removedKey = cartItemRowToRemove.dataset.key;
    const removedGroup = cartItemRowToRemove.dataset.bulkOrderGroup;
    const cartItemRows = this.#getCartItemRows();
    const groupHasOtherParents = removedGroup
      ? cartItemRows.some(
          (row) => row.dataset.bulkOrderGroup === removedGroup && row.dataset.key && row.dataset.key !== removedKey
        )
      : false;
    const linkedFeeRowsToRemove = removedKey
      ? cartItemRows.filter(
          (row) =>
            (row.dataset.feeParentKey === removedKey && !row.dataset.feeParentGroup) ||
            (removedGroup && row.dataset.feeParentGroup === removedGroup && !groupHasOtherParents)
        )
      : [];
    const linkedFeeKeysToRemove = linkedFeeRowsToRemove
      .map((row) => row.dataset.key)
      .filter((key) => key);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      lineKey: cartItemRowToRemove.dataset.key,
      keysToClear: linkedFeeKeysToRemove,
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...linkedFeeRowsToRemove,
      // Get all nested lines of the row to remove
      ...cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.lineKey] - The cart line key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.keysToClear] - Extra cart line keys to remove with this line.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    try {
      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const extraKeysToClear = Array.isArray(config.keysToClear) ? config.keysToClear.filter((key) => key) : [];
      const cartItemRows = this.#getCartItemRows();
      const changedRow = config.lineKey
        ? cartItemRows.find((row) => row.dataset.key === config.lineKey)
        : null;
      const changedGroup = changedRow?.dataset.bulkOrderGroup;
      const groupQuantity = changedGroup
        ? cartItemRows.reduce((total, row) => {
            if (row.dataset.bulkOrderGroup !== changedGroup || !row.dataset.key) return total;
            if (row.dataset.key === config.lineKey) return total + quantity;
            return total + (parseInt(row.dataset.cartQuantity || '0', 10) || 0);
          }, 0)
        : 0;
      const linkedFeeQuantityUpdates =
        config.lineKey
          ? cartItemRows
              .filter(
                (row) =>
                  row.dataset.feeQuantityMode === 'per-item' &&
                  row.dataset.key &&
                  (row.dataset.feeParentKey === config.lineKey ||
                    (changedGroup && row.dataset.feeParentGroup === changedGroup))
              )
              .map((row) => [
                row.dataset.key,
                changedGroup && row.dataset.feeParentGroup === changedGroup ? groupQuantity : quantity,
              ])
          : [];
      const shouldUseCartUpdate =
        !!config.lineKey && (extraKeysToClear.length > 0 || linkedFeeQuantityUpdates.length > 0);
      const bodyPayload = shouldUseCartUpdate
        ? {
            updates: {
              [config.lineKey]: quantity,
              ...Object.fromEntries(linkedFeeQuantityUpdates),
              ...Object.fromEntries(extraKeysToClear.map((key) => [key, 0])),
            },
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
        : {
            line: line,
            quantity: quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          };
      const body = JSON.stringify(bodyPayload);

      cartTotal?.shimmer();

      const lineId = config.lineKey ?? this.#getCartItemRow(line)?.dataset.key ?? '';
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(shouldUseCartUpdate ? Theme.routes.cart_update_url : Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#refreshCartSection();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#groupLinkedFeeRows();
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      try {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles minimum quantity error for cart items.
   * @param {number} line - The line item index.
   * @param {number} minimumQuantity - The minimum required quantity.
   */
  #handleCartMinimumQuantityError = (line, minimumQuantity) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    // Get error message template from theme translations or use default
    const errorMessage = Theme.translations?.quantity_error_min
      ? Theme.translations.quantity_error_min.replace('{{ minimum }}', minimumQuantity.toString())
      : `Minimum quantity required: ${minimumQuantity}`;

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');

    // Hide error after 10 seconds (same duration as other errors)
    setTimeout(() => {
      cartItemErrorContainer.classList.add('hidden');
    }, 10000);
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.#getCartItemRows().map((row) => row.dataset.key));

          if (wasEmptyCartDrawer) {
            await startViewTransition(async () => {
              await morphSection(this.sectionId, cartItemsHtml, morphOptions);
              this.#groupLinkedFeeRows();
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            this.#groupLinkedFeeRows();
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.#getCartItemRows()) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + item.quantity);
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      const variantId = selector.getAttribute('data-variant-id');
      const quantity = quantitiesByVariant.get(variantId) || 0;
      if ('setCartQuantity' in selector && typeof selector.setCartQuantity === 'function') {
        selector.setCartQuantity(quantity);
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
